import React, { type ReactNode } from "react"
import { cn } from "../styles/utils"
import { CloseButton } from "./CloseButton"
import { Text } from "./Text"
import {
  Popover as GluestackPopover,
  PopoverArrow,
  PopoverBackdrop,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
} from "./gluestack/popover"

export interface PopoverProps {
  isVisible: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  size?: "xs" | "sm" | "md" | "lg" | "full"
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top left"
    | "top right"
    | "bottom left"
    | "bottom right"
    | "left top"
    | "left bottom"
    | "right top"
    | "right bottom"
  showCloseButton?: boolean
  footer?: ReactNode
  bodyClassName?: string
  contentClassName?: string
  closeButtonClassName?: string
  footerClassName?: string
  headerClassName?: string
  closeOnBackdropPress?: boolean
  showArrow?: boolean
  arrowClassName?: string
  shouldOverlapWithTrigger?: boolean
  trapFocus?: boolean
  focusScope?: boolean
  trigger?: () => React.ReactElement
}

export const Popover: React.FC<PopoverProps> = ({
  isVisible,
  onClose,
  title,
  children,
  size = "md",
  placement = "bottom",
  showCloseButton = true,
  footer,
  bodyClassName,
  contentClassName,
  closeButtonClassName,
  footerClassName,
  headerClassName,
  closeOnBackdropPress = true,
  showArrow = false,
  arrowClassName,
  shouldOverlapWithTrigger = false,
  trapFocus = true,
  focusScope = true,
  trigger,
}) => {
  return (
    <GluestackPopover
      isOpen={isVisible}
      onClose={onClose}
      size={size}
      placement={placement}
      trigger={trigger}
      shouldOverlapWithTrigger={shouldOverlapWithTrigger}
      trapFocus={trapFocus}
      focusScope={focusScope}
    >
      <PopoverBackdrop onPress={closeOnBackdropPress ? onClose : undefined} />
      <PopoverContent className={cn("bg-background-0", contentClassName)}>
        {showArrow && <PopoverArrow className={arrowClassName} />}
        {title && (
          <PopoverHeader
            className={cn(
              "flex-row justify-between items-center p-4 border-b border-outline-200",
              headerClassName,
            )}
          >
            <Text className="text-lg font-bold">{title}</Text>
            {showCloseButton && (
              <CloseButton
                onPress={onClose}
                variant="default"
                className={closeButtonClassName}
              />
            )}
          </PopoverHeader>
        )}
        <PopoverBody className={cn("p-4", bodyClassName)}>
          {children}
        </PopoverBody>
        {footer && (
          <PopoverFooter
            className={cn("border-t border-gray-200 p-4", footerClassName)}
          >
            {footer}
          </PopoverFooter>
        )}
      </PopoverContent>
    </GluestackPopover>
  )
}
